import React, { useContext, useEffect, useRef } from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import useRefsInit from "../hooks/useRefsInit.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import {
  ALERT_TYPES,
  GRID_FIELDS_TO_OPEN_EDITOR,
  VIEW_TYPES
} from "../../other/constants/OtherKeys.jsx";
import { GRANTS } from "../../other/constants/PrivilegeTypes.jsx";
import { cellAvatarTooltipConfig } from "../../other/utils/utils.jsx";
import AuthContext from "../context/authContext.jsx";
import { cellMenuItemRemoveRow, processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import { callOnLoadOnColumns, canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import { REMINDERS_GRID_CONFIG } from "../../other/constants/ReminderViewGridConfig.js";
import { REMINDER_VIEW_COLUMN } from "../../other/constants/ReminderViewColumns.jsx";
import { ALERT_ENTITIES } from "./AlertPage.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { Store } from "@bryntum/schedulerpro";
import { SwivuStoreArchiveMixin } from "../../bryntum-lib/stores/mixins/SwivuStoreArchiveMixin.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";

let timeoutClear;

const RemindersPage = () => {
  const gridRef = useRef();
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const {user} = useContext(AuthContext);
  const context  = useContext(BryntumContext);
  const { project, editorHandler, entityManager } = context;
  useEnableViewTracking(gridRef, VIEW_TYPES.reminder, isRefsInit, null);

  useEffect(() => {
    const reminderGrid = gridRef.current.instance;
    if (project && reminderGrid) {
      /** On Task Archived backend also archived the links reminder. that's why we're using SwivuStoreArchiveMixin */
      reminderGrid.store = new (SwivuStoreArchiveMixin(Store))({
        data: project.reminderStore.query(
          (reminder) =>
            reminder.alertType === ALERT_TYPES.REMINDER &&
            reminder.alertTag === "Reminder",
          true
        )
      })

      // after coming from a different view the group doesn't apply
      timeoutClear = setTimeout(() => {
        reminderGrid.store.group({
          field: "status",
          ascending: true,
          hidden: true,
        });
      }, 0);
    }

    return () => {
      clearTimeout(timeoutClear);
    }
  }, [project, gridRef, user]);

  useEffect(() => {
    const reminderGrid = gridRef.current.instance;
    if (reminderGrid) {
      reminderGrid.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Reminder",
        editorHandler,
        grid: reminderGrid,
        user
      });
      // reminderGrid in this page not taking an original store instead using own store hence default remove functionality didn't work
      reminderGrid.features.cellMenu.items.removeRow = cellMenuItemRemoveRow({
        onItem() {
          const selectedIds = reminderGrid.selectedRecords.map((r) => r.id);
          reminderGrid.store.remove(selectedIds);
          project.reminderStore.remove(selectedIds);
        }
      });
      callOnLoadOnColumns(reminderGrid.columns, project, { editorHandler });
    }
  }, [editorHandler, gridRef, project]);

  const cellClickHandler = debounce(({ column, record, event }) => {
    if (user && user.can(GRANTS.grant_four) && !record.isGroupHeader) {
      if (column.isAlertBellColumn) {
        record.updateSeenStatus();
      } else if (column.type === WIDGET_KEYS.recurringIntervalColumn) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: record,
          column,
          event
        }, { by: "recordType", useExisting: true })
      } else if (column.id === GRID_FIELDS_TO_OPEN_EDITOR.RECIPIENT_NAME && canOpenEditor(column, record)) {
        const idKey = "swivu-recipient-editor-button";
        const target = event.target;
        const { id }  = (target.id === idKey ? target.dataset : target.closest(`#${idKey}`)?.dataset) ?? {};
        if (id) {
          const user = project.boardUsersStore.getById(id);
          editorHandler.openEditor(EDITOR_KEYS.userEditor, user, { user: user });
        }
      }
    }
  }, 500)

  const onOpenEditorByEntityType = ({record, column}) => {
    if (canOpenEditor(column, record, [GRID_FIELDS_TO_OPEN_EDITOR.ENTITY_NAME])) {
      const editor = ALERT_ENTITIES[record.entityType];
      if (editor.entityManagerPage) {
        entityManager.openEntityManager({ entityType: editor.entityManagerPage, entityId: record.entity.id });
      } else {
        editorHandler.openEditor(editor.EDITOR_KEY, record.entity, {
          [editor.recordKey]: record.entity,
          selectTabByTypeOnOpen: editor[record.alertType],
        });
      }
    }
  }

  const onBeforeCellEditStart = ({ editorContext: { record, column } }) => {
    const repeatCountStatus = record.recurringInterval === 0 && column.id === "repeatCount";
    return !repeatCountStatus
  }

  return (
      <>
        <ViewSpecificHeader />
        <BryntumGrid
          ref={setRefs(gridRef)}
          {...REMINDERS_GRID_CONFIG}
          columns={REMINDER_VIEW_COLUMN}
          selectionMode={{
            checkbox: false
          }}
          stateId
          disabled={user && !user.can(GRANTS.grant_four)}
          readOnly={user && !user.can(GRANTS.grant_four)}
          onCellClick={cellClickHandler}
          onCellDblClick={onOpenEditorByEntityType}
          onBeforeCellEditStart={onBeforeCellEditStart}
          preserveScrollOnDatasetChange={true}
          cellTooltipFeature={cellAvatarTooltipConfig}
          searchFeature={true}
        />
    </>
  )
}

export default RemindersPage;
